/**
 * Time Machine - Signal Replay & Strategy Optimizer Dashboard
 * SESSION 75: Isolated backtesting engine UI
 * SESSION 76: Added Server File Selector
 * SESSION 91: Disabled during market hours to prevent performance impact
 * SESSION 95: Added Strategy Optimizer (Option B Grid Search)
 * 
 * Features:
 * - CSV file upload/selection from server recordings
 * - Dual Execution Modes:
 *   1. Standard Replay - Single pass with current weights
 *   2. Strategy Optimizer - Grid search for optimal weights
 * - Morning Regime Context analysis
 * - Daily Strategy Card with best configuration
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Play, 
  Pause, 
  Square, 
  Upload, 
  Clock,
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Server,
  FolderOpen,
  ChevronDown,
  ShieldAlert,
  Zap,
  Target,
  BarChart3,
  Trophy,
  Percent,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  Settings2,
  Download,
  Trash2,
  CheckSquare,
  Square as SquareIcon,
  RefreshCw  // SESSION 104: Added for history refresh
} from 'lucide-react';
import { useMarket } from '../contexts/MarketProvider';
import HierarchyBacktestTab from './HierarchyBacktestTab';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TimeMachine = () => {
  // SESSION 91: Check if market is open to disable Time Machine
  const marketContext = useMarket();
  const connectionStatus = marketContext?.connectionStatus || {};
  const isMarketOpen = connectionStatus?.isMarketOpen ?? false;
  
  // State
  const [status, setStatus] = useState({
    state: 'idle',
    has_data: false,
    total_ticks: 0,
    processed_ticks: 0,
    progress_percent: 0,
    signals_generated: 0,
    playback_speed: 1.0,
    csv_filename: '',
    symbol: 'NIFTY'
  });
  
  const [signals, setSignals] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSpeed, setSelectedSpeed] = useState(1.0);
  const [isLoading, setIsLoading] = useState(true);
  
  // SESSION 76: Server file selector state
  const [serverFiles, setServerFiles] = useState([]);
  const [selectedServerFile, setSelectedServerFile] = useState('');
  const [loadingServerFiles, setLoadingServerFiles] = useState(false);
  const [loadingFromServer, setLoadingFromServer] = useState(false);
  const [showServerDropdown, setShowServerDropdown] = useState(false);
  
  // SESSION 95: Strategy Optimizer state
  const [optimizerResult, setOptimizerResult] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [activeMode, setActiveMode] = useState(null); // 'standard' | 'optimizer'
  
  // SESSION 95B: Multi-select for bulk operations
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  
  // SESSION 104: Weight History tab state
  const [activeTab, setActiveTab] = useState('optimizer'); // 'optimizer' | 'history'
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyPatterns, setHistoryPatterns] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [savingToHistory, setSavingToHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ symbol: '', days: 90 });
  
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const dropdownRef = useRef(null);

  // SESSION 76: Fetch available server files
  const fetchServerFiles = useCallback(async () => {
    setLoadingServerFiles(true);
    try {
      const response = await fetch(`${API_URL}/api/tick-logger/files`);
      const data = await response.json();
      setServerFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching server files:', err);
    } finally {
      setLoadingServerFiles(false);
    }
  }, []);

  // SESSION 76: Load file from server
  const handleLoadFromServer = async () => {
    if (!selectedServerFile) {
      setError('Please select a file from the dropdown');
      return;
    }

    setLoadingFromServer(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/replay/load-server-file?filename=${encodeURIComponent(selectedServerFile)}`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setStatus(prev => ({
          ...prev,
          has_data: true,
          total_ticks: data.total_ticks,
          csv_filename: selectedServerFile,
          state: 'idle'
        }));
        setSignals([]);
        setOptimizerResult(null);
        setActiveMode(null);
        setShowServerDropdown(false);
      } else {
        setError(data.error || 'Failed to load file');
      }
    } catch (err) {
      setError('Failed to load file from server: ' + err.message);
    } finally {
      setLoadingFromServer(false);
    }
  };

  // SESSION 95B: Toggle file selection for multi-select
  const toggleFileSelection = (filename, e) => {
    e.stopPropagation();
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filename)) {
        newSet.delete(filename);
      } else {
        newSet.add(filename);
      }
      return newSet;
    });
  };

  // SESSION 95B: Select/Deselect all files
  const toggleSelectAll = () => {
    if (selectedFiles.size === serverFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(serverFiles.map(f => f.filename)));
    }
  };

  // SESSION 95B: Delete single file
  const handleDeleteFile = async (filename, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/tick-logger/delete/${encodeURIComponent(filename)}`,
        { method: 'DELETE' }
      );
      const data = await response.json();
      
      if (data.success) {
        // Refresh file list
        fetchServerFiles();
        // Clear selection if deleted file was selected
        if (selectedServerFile === filename) {
          setSelectedServerFile('');
        }
        setSelectedFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(filename);
          return newSet;
        });
      } else {
        setError(data.detail || 'Failed to delete file');
      }
    } catch (err) {
      setError('Failed to delete file: ' + err.message);
    }
  };

  // SESSION 95B: Bulk delete selected files
  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) {
      setError('No files selected');
      return;
    }
    
    const fileList = Array.from(selectedFiles).join('\n- ');
    if (!window.confirm(`Are you sure you want to delete ${selectedFiles.size} file(s)?\n\n- ${fileList}`)) {
      return;
    }
    
    setIsDeleting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/tick-logger/delete-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.from(selectedFiles))
      });
      const data = await response.json();
      
      if (data.deleted_count > 0) {
        // Refresh file list
        fetchServerFiles();
        // Clear selections
        setSelectedFiles(new Set());
        if (selectedFiles.has(selectedServerFile)) {
          setSelectedServerFile('');
        }
      }
      
      if (data.errors && data.errors.length > 0) {
        setError(`Deleted ${data.deleted_count} files. Errors: ${data.errors.map(e => e.filename).join(', ')}`);
      }
    } catch (err) {
      setError('Failed to delete files: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // SESSION 95B: Download a single file using iframe (most reliable method)
  const handleSingleDownload = (filename) => {
    console.log('📥 Starting download for:', filename);
    const downloadUrl = `${API_URL}/api/tick-logger/download/${encodeURIComponent(filename)}`;
    console.log('📥 Download URL:', downloadUrl);
    
    // Create hidden iframe to trigger download without navigation
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);
    
    // Remove iframe after download starts
    setTimeout(() => {
      iframe.remove();
      console.log('📥 Download triggered');
    }, 3000);
  };

  // SESSION 95B: Bulk download selected files as ZIP
  const handleBulkDownload = async () => {
    if (selectedFiles.size === 0) {
      setError('No files selected');
      return;
    }
    
    // For single file, use direct download
    if (selectedFiles.size === 1) {
      const filename = Array.from(selectedFiles)[0];
      await handleSingleDownload(filename);
      return;
    }
    
    // For multiple files, use ZIP download
    setIsBulkDownloading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/tick-logger/download-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.from(selectedFiles))
      });
      
      if (response.ok) {
        // Get the blob and trigger download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `tick_data_${new Date().toISOString().slice(0,10)}.zip`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup after a short delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          a.remove();
        }, 100);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to download files');
      }
    } catch (err) {
      setError('Failed to download files: ' + err.message);
    } finally {
      setIsBulkDownloading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowServerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/replay/status`);
      const data = await response.json();
      setStatus({
        state: data.state || 'idle',
        has_data: data.has_data || false,
        total_ticks: data.total_ticks || 0,
        processed_ticks: data.processed_ticks || 0,
        progress_percent: data.progress_percent || 0,
        signals_generated: data.signals_generated || 0,
        playback_speed: data.playback_speed || 1.0,
        csv_filename: data.csv_filename || '',
        symbol: data.symbol || 'NIFTY'
      });
      setIsLoading(false);
      
      if (data.state === 'playing' || data.state === 'completed') {
        fetchSignals();
      }
    } catch (err) {
      console.error('Error fetching replay status:', err);
      setIsLoading(false);
    }
  }, []);

  // Fetch signals
  const fetchSignals = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/replay/signals?limit=50`);
      const data = await response.json();
      setSignals(data.signals || []);
    } catch (err) {
      console.error('Error fetching signals:', err);
    }
  }, []);

  // Poll for updates when playing
  useEffect(() => {
    fetchStatus();
    fetchServerFiles();
    
    pollIntervalRef.current = setInterval(() => {
      if (status.state === 'playing') {
        fetchStatus();
      }
    }, 500);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [status.state, fetchStatus]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/replay/upload?symbol=NIFTY`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setStatus(prev => ({
          ...prev,
          has_data: true,
          total_ticks: data.total_ticks,
          csv_filename: file.name,
          state: 'idle'
        }));
        setSignals([]);
        setOptimizerResult(null);
        setActiveMode(null);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError('Failed to upload file: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Standard Replay Controls
  const handleStart = async () => {
    try {
      setError(null);
      setActiveMode('standard');
      setOptimizerResult(null);
      const response = await fetch(`${API_URL}/api/replay/start?speed=${selectedSpeed}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.error);
      }
      fetchStatus();
    } catch (err) {
      setError('Failed to start replay');
    }
  };

  const handlePause = async () => {
    try {
      await fetch(`${API_URL}/api/replay/pause`, { method: 'POST' });
      fetchStatus();
    } catch (err) {
      setError('Failed to pause replay');
    }
  };

  const handleResume = async () => {
    try {
      await fetch(`${API_URL}/api/replay/resume`, { method: 'POST' });
      fetchStatus();
    } catch (err) {
      setError('Failed to resume replay');
    }
  };

  const handleStop = async () => {
    try {
      await fetch(`${API_URL}/api/replay/stop`, { method: 'POST' });
      fetchStatus();
    } catch (err) {
      setError('Failed to stop replay');
    }
  };

  const handleSpeedChange = async (speed) => {
    setSelectedSpeed(speed);
    if (status.state === 'playing') {
      try {
        await fetch(`${API_URL}/api/replay/speed?speed=${speed}`, { method: 'POST' });
        fetchStatus();
      } catch (err) {
        console.error('Failed to change speed');
      }
    }
  };

  // SESSION 95: Strategy Optimizer
  const handleRunOptimizer = async () => {
    if (!selectedServerFile && !status.csv_filename) {
      setError('Please select or upload a file first');
      return;
    }

    setIsOptimizing(true);
    setError(null);
    setActiveMode('optimizer');
    setSignals([]);

    try {
      // Use server file if selected, otherwise use already loaded file
      const filename = selectedServerFile || status.csv_filename;
      const response = await fetch(
        `${API_URL}/api/replay/optimize-server-file?filename=${encodeURIComponent(filename)}`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setOptimizerResult(data);
      } else {
        setError(data.error || 'Optimization failed');
      }
    } catch (err) {
      setError('Failed to run optimizer: ' + err.message);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Get state color
  const getStateColor = (state) => {
    switch (state) {
      case 'playing': return 'text-green-400';
      case 'paused': return 'text-yellow-400';
      case 'completed': return 'text-blue-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  // Get signal color
  const getSignalColor = (type) => {
    switch (type) {
      case 'BUY': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'SELL': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'VETO': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  // SESSION 104: Fetch optimization history
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams();
      if (historyFilter.symbol) params.append('symbol', historyFilter.symbol);
      params.append('days', historyFilter.days.toString());
      
      const response = await fetch(`${API_URL}/api/replay/optimization/history?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setHistoryRecords(data.records || []);
        setHistoryPatterns(data.patterns || null);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyFilter]);

  // SESSION 104: Save current optimization to history
  const saveToHistory = async () => {
    if (!optimizerResult?.best_config) return;
    
    setSavingToHistory(true);
    try {
      const best = optimizerResult.best_config;
      const regime = optimizerResult.morning_regime;
      const meta = optimizerResult.meta;
      
      // Extract date from CSV filename or use today
      let dateAnalyzed = new Date().toISOString().split('T')[0];
      if (meta?.csv_file) {
        const dateMatch = meta.csv_file.match(/(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) dateAnalyzed = dateMatch[1];
      }
      
      const payload = {
        symbol: meta?.symbol || status.symbol || 'NIFTY',
        date_analyzed: dateAnalyzed,
        weights: best.weights,
        metrics: {
          total_pnl: best.total_pnl,
          win_rate: best.win_rate,
          profit_factor: best.profit_factor,
          total_trades: best.total_trades,
        },
        regime: regime?.regime || 'UNKNOWN',
        csv_file: meta?.csv_file || '',
      };
      
      const response = await fetch(`${API_URL}/api/replay/optimization/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      if (data.success) {
        setError(null);
        // Refresh history if on history tab
        if (activeTab === 'history') {
          fetchHistory();
        }
        alert('Saved to history!');
      } else {
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSavingToHistory(false);
    }
  };

  // SESSION 104: Delete history record
  const deleteHistoryRecord = async (recordId) => {
    if (!window.confirm('Delete this optimization record?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/replay/optimization/${recordId}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      
      if (data.success) {
        fetchHistory();
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  // SESSION 104: Load history when tab changes
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  // SESSION 104: Check if a date is expiry day for a given symbol
  // NIFTY expiry: Thursday, BANKNIFTY expiry: Wednesday (or Tuesday for BANKNIFTY before Nov 2024)
  const isExpiryDay = (dateStr, symbol) => {
    try {
      const date = new Date(dateStr);
      const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, ... 4=Thursday, 3=Wednesday
      
      if (symbol === 'NIFTY') {
        return dayOfWeek === 4; // Thursday
      } else if (symbol === 'BANKNIFTY') {
        // BANKNIFTY moved from Tuesday to Wednesday in late 2024
        // For simplicity, we check Wednesday (current) - adjust if needed for historical data
        return dayOfWeek === 3; // Wednesday
      }
      return false;
    } catch {
      return false;
    }
  };

  // SESSION 91: If market is open, show disabled message
  if (isMarketOpen) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center text-white p-8" data-testid="time-machine-disabled">
        <ShieldAlert className="w-16 h-16 text-amber-500 mb-4" />
        <h2 className="text-2xl font-bold text-amber-500 mb-2">Time Machine Disabled</h2>
        <p className="text-gray-400 text-center max-w-md mb-4">
          Time Machine is disabled during market hours (09:15 - 15:30 IST) to prevent 
          performance impact on live trading operations.
        </p>
        <div className="bg-[#12121a] border border-gray-800 rounded-lg p-4 text-sm">
          <p className="text-cyan-400 font-semibold mb-2">Available After Market Close:</p>
          <ul className="text-gray-400 space-y-1">
            <li>• Weekdays: After 3:30 PM IST</li>
            <li>• Weekends: All day</li>
            <li>• Market Holidays: All day</li>
          </ul>
        </div>
      </div>
    );
  }

  // Render Strategy Card (SESSION 95)
  const renderStrategyCard = () => {
    if (!optimizerResult || !optimizerResult.best_config) return null;

    const best = optimizerResult.best_config;
    const baseline = optimizerResult.baseline_config;
    const regime = optimizerResult.morning_regime;
    const meta = optimizerResult.meta;

    const pnlDiff = baseline ? (best.total_pnl - baseline.total_pnl) : 0;

    return (
      <div className="space-y-6 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">{/* SESSION 104: Added scroll */}
        {/* Best Configuration Card */}
        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#12121a] rounded-xl p-6 border border-yellow-500/30">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-yellow-400" />
            <div>
              <h3 className="text-xl font-bold text-yellow-400">Best Weight Configuration</h3>
              <p className="text-sm text-gray-400">Optimized for {meta?.date_analyzed || 'this session'}</p>
            </div>
          </div>

          {/* Weight Distribution - SESSION 104: 4 components */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{best.weights?.order_flow_weight}%</div>
              <div className="text-xs text-gray-400 mt-1">Order Flow</div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-400">{best.weights?.profile_context_weight}%</div>
              <div className="text-xs text-gray-400 mt-1">Profile Context</div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-cyan-400">{best.weights?.profile_levels_weight}%</div>
              <div className="text-xs text-gray-400 mt-1">Profile Levels</div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{best.weights?.gex_weight}%</div>
              <div className="text-xs text-gray-400 mt-1">GEX</div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-black/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                <Target className="w-4 h-4" />
                Profit Factor
              </div>
              <div className="text-2xl font-bold text-cyan-400">
                {best.profit_factor === Infinity ? '∞' : best.profit_factor?.toFixed(2)}
              </div>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                <DollarSign className="w-4 h-4" />
                Total P&L
              </div>
              <div className={`text-2xl font-bold ${best.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {best.total_pnl >= 0 ? '+' : ''}{best.total_pnl?.toFixed(1)} pts
              </div>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                <Percent className="w-4 h-4" />
                Win Rate
              </div>
              <div className="text-2xl font-bold text-white">
                {best.win_rate?.toFixed(1)}%
              </div>
            </div>
            <div className="bg-black/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                <BarChart3 className="w-4 h-4" />
                Trades
              </div>
              <div className="text-2xl font-bold text-white">
                <span className="text-green-400">{best.winning_trades}</span>
                <span className="text-gray-500">/</span>
                <span className="text-red-400">{best.losing_trades}</span>
              </div>
            </div>
          </div>

          {/* Comparison with baseline */}
          {baseline && (
            <div className="mt-4 p-3 bg-black/20 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">vs Current Config ({baseline.weights?.order_flow_weight}/{baseline.weights?.profile_context_weight}/{baseline.weights?.profile_levels_weight}/{baseline.weights?.gex_weight})</span>
                <span className={`text-lg font-bold flex items-center gap-1 ${pnlDiff >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {pnlDiff >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {pnlDiff >= 0 ? '+' : ''}{pnlDiff.toFixed(1)} pts potential
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Morning Regime Context */}
        {regime && (
          <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <Activity className="w-6 h-6 text-purple-400" />
              <h3 className="text-lg font-semibold">Morning Regime Analysis</h3>
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                regime.regime?.includes('BULLISH') ? 'bg-green-500/20 text-green-400' :
                regime.regime?.includes('BEARISH') ? 'bg-red-500/20 text-red-400' :
                regime.regime?.includes('RANGING') ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {regime.regime}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-sm text-gray-400">Delta Slope</div>
                <div className={`text-xl font-bold ${regime.avg_delta_slope > 0 ? 'text-green-400' : regime.avg_delta_slope < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                  {regime.avg_delta_slope?.toFixed(1)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-400">Volatility</div>
                <div className="text-xl font-bold text-white">{regime.tick_volatility?.toFixed(1)}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-400">VIX Level</div>
                <div className="text-xl font-bold text-white">{regime.vix_level?.toFixed(1)}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-400">GEX Regime</div>
                <div className={`text-xl font-bold ${
                  regime.gex_regime === 'POSITIVE' ? 'text-green-400' :
                  regime.gex_regime === 'NEGATIVE' ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {regime.gex_regime}
                </div>
              </div>
            </div>

            {regime.recommended_weights && (
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-purple-400 mb-1">
                  <Settings2 className="w-4 h-4" />
                  Regime-Based Recommendation
                </div>
                <div className="text-white">
                  OF: {regime.recommended_weights.order_flow_weight}% | 
                  PC: {regime.recommended_weights.profile_context_weight}% | 
                  PL: {regime.recommended_weights.profile_levels_weight}% | 
                  GEX: {regime.recommended_weights.gex_weight}%
                </div>
              </div>
            )}
          </div>
        )}

        {/* Trade Log */}
        {best.trades && best.trades.length > 0 && (
          <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />
              Simulated Trades ({best.trades.length})
            </h3>
            <div className="overflow-auto max-h-[300px]">
              <table className="w-full text-sm">
                <thead className="text-gray-400 border-b border-gray-800 sticky top-0 bg-[#12121a]">
                  <tr>
                    <th className="text-left py-2 px-3">Entry</th>
                    <th className="text-left py-2 px-3">Direction</th>
                    <th className="text-right py-2 px-3">Entry Price</th>
                    <th className="text-left py-2 px-3">Exit Time</th>
                    <th className="text-right py-2 px-3">Exit Price</th>
                    <th className="text-right py-2 px-3">P&L</th>
                    <th className="text-left py-2 px-3">Exit Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {best.trades.map((trade, idx) => (
                    <tr key={idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="py-2 px-3 text-gray-400 font-mono text-xs">
                        {trade.entry_time?.split(' ')[1]?.substring(0, 8) || '-'}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          trade.direction === 'LONG' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {trade.direction === 'LONG' ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
                          {trade.direction}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-gray-300">
                        {trade.entry_price?.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-gray-400 font-mono text-xs">
                        {trade.exit_time?.split(' ')[1]?.substring(0, 8) || '-'}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-gray-300">
                        {trade.exit_price?.toLocaleString() || '-'}
                      </td>
                      <td className={`py-2 px-3 text-right font-mono font-bold ${trade.pnl_points >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {trade.pnl_points >= 0 ? '+' : ''}{trade.pnl_points?.toFixed(1)}
                      </td>
                      <td className="py-2 px-3 text-gray-500 text-xs">
                        {trade.exit_reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Meta Info */}
        <div className="text-xs text-gray-500 text-center">
          Analyzed {meta?.total_ticks_analyzed?.toLocaleString()} ticks | 
          Tested {meta?.configs_tested} configurations | 
          Completed in {meta?.optimization_time_seconds?.toFixed(2)}s
        </div>

        {/* SESSION 104: Save to History Button */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={saveToHistory}
            disabled={savingToHistory}
            className="flex items-center gap-2 px-6 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-600/50 rounded-lg font-medium transition-colors"
            data-testid="save-to-history-btn"
          >
            {savingToHistory ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Save to History
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-6 overflow-y-auto max-h-screen" data-testid="time-machine-page">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          <span className="ml-3 text-gray-400">Loading Time Machine...</span>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-8 h-8 text-blue-400" />
              <h1 className="text-2xl font-bold">Time Machine</h1>
              <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded">
                Historical Research Hub
              </span>
            </div>
            <p className="text-gray-400 text-sm">
              Backtest strategies and optimize weights with historical data. Zero impact on live trading.
            </p>
          </div>

          {/* Unified Data Input Section */}
          <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-400" />
              Load Historical Data
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Server File Selector */}
              <div ref={dropdownRef}>
                <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  Load from Recorded Files
                </label>
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowServerDropdown(!showServerDropdown);
                      if (!showServerDropdown) fetchServerFiles();
                    }}
                    disabled={status.state === 'playing' || isOptimizing}
                    className="w-full py-3 px-4 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-800/50
                               rounded-lg font-medium transition-colors flex items-center justify-between
                               border border-gray-700"
                    data-testid="server-file-dropdown-btn"
                  >
                    <span className="flex items-center gap-2 text-sm">
                      <FolderOpen className="w-4 h-4 text-green-400" />
                      {selectedServerFile || 'Select recorded file...'}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showServerDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showServerDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg 
                                    shadow-xl max-h-80 overflow-hidden flex flex-col">
                      {loadingServerFiles ? (
                        <div className="p-4 text-center text-gray-400">
                          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                          Loading files...
                        </div>
                      ) : serverFiles.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No recorded files found.
                        </div>
                      ) : (
                        <>
                          {/* Bulk Action Header */}
                          <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-2 flex items-center justify-between z-10">
                            <button
                              onClick={toggleSelectAll}
                              className="flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors"
                              data-testid="select-all-btn"
                            >
                              {selectedFiles.size === serverFiles.length ? (
                                <CheckSquare className="w-4 h-4 text-cyan-400" />
                              ) : (
                                <SquareIcon className="w-4 h-4" />
                              )}
                              {selectedFiles.size === serverFiles.length ? 'Deselect All' : 'Select All'}
                            </button>
                            
                            {selectedFiles.size > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 mr-2">{selectedFiles.size} selected</span>
                                <button
                                  onClick={handleBulkDownload}
                                  disabled={isBulkDownloading}
                                  className="p-1.5 text-cyan-400 hover:bg-cyan-600/20 rounded transition-colors"
                                  title="Download selected as ZIP"
                                  data-testid="bulk-download-btn"
                                >
                                  {isBulkDownloading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={handleBulkDelete}
                                  disabled={isDeleting}
                                  className="p-1.5 text-red-400 hover:bg-red-600/20 rounded transition-colors"
                                  title="Delete selected files"
                                  data-testid="bulk-delete-btn"
                                >
                                  {isDeleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* File List */}
                          <div className="overflow-auto max-h-60">
                            {serverFiles.map((file) => (
                              <div
                                key={file.filename}
                                className={`flex items-center px-3 py-2.5 hover:bg-gray-700 transition-colors
                                            border-b border-gray-700/50 last:border-0
                                            ${selectedServerFile === file.filename ? 'bg-blue-600/20' : ''}
                                            ${selectedFiles.has(file.filename) ? 'bg-cyan-600/10' : ''}`}
                              >
                                {/* Checkbox */}
                                <button
                                  onClick={(e) => toggleFileSelection(file.filename, e)}
                                  className="p-1 mr-2 text-gray-400 hover:text-cyan-400 transition-colors"
                                  data-testid={`checkbox-${file.filename}`}
                                >
                                  {selectedFiles.has(file.filename) ? (
                                    <CheckSquare className="w-4 h-4 text-cyan-400" />
                                  ) : (
                                    <SquareIcon className="w-4 h-4" />
                                  )}
                                </button>
                                
                                {/* File name (clickable to select for loading) */}
                                <button
                                  onClick={() => {
                                    setSelectedServerFile(file.filename);
                                    setShowServerDropdown(false);
                                  }}
                                  className="flex-1 text-left min-w-0"
                                >
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-white truncate">
                                      {file.filename}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                      {file.size_mb > 0 ? `${file.size_mb}MB` : `${file.size_bytes}B`}
                                    </span>
                                  </div>
                                </button>
                                
                                {/* Action buttons */}
                                <div className="flex items-center ml-2 flex-shrink-0">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSingleDownload(file.filename);
                                    }}
                                    className="p-1.5 text-gray-400 hover:text-cyan-400 hover:bg-gray-600 rounded transition-colors"
                                    title={`Download ${file.filename}`}
                                    data-testid={`download-${file.filename}`}
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteFile(file.filename, e)}
                                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded transition-colors"
                                    title={`Delete ${file.filename}`}
                                    data-testid={`delete-${file.filename}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedServerFile && (
                  <button
                    onClick={handleLoadFromServer}
                    disabled={loadingFromServer || status.state === 'playing' || isOptimizing}
                    className="w-full mt-2 py-2 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-700
                               rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    data-testid="load-server-file-btn"
                  >
                    {loadingFromServer ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Load Selected File
                      </>
                    )}
                  </button>
                )}
              </div>
              
              {/* Manual Upload */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Or Upload CSV File
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="csv-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || status.state === 'playing' || isOptimizing}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 
                             rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Upload CSV File
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Loaded File Info */}
            {(status.csv_filename || selectedServerFile) && (
              <div className="mt-4 p-3 bg-gray-800/50 rounded-lg text-sm flex items-center justify-between">
                <div>
                  <span className="text-gray-400">Loaded: </span>
                  <span className="text-white font-medium">{status.csv_filename || selectedServerFile}</span>
                  {status.total_ticks > 0 && (
                    <span className="text-gray-500 ml-2">({status.total_ticks.toLocaleString()} ticks)</span>
                  )}
                </div>
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
            )}
          </div>

          {/* Dual Execution Mode Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Button 1: Standard Replay */}
            <button
              onClick={handleStart}
              disabled={!status.has_data && !selectedServerFile || status.state === 'playing' || isOptimizing}
              className={`p-6 rounded-xl border transition-all flex items-center gap-4 ${
                activeMode === 'standard' 
                  ? 'bg-blue-600/20 border-blue-500 text-blue-400' 
                  : 'bg-[#12121a] border-gray-800 hover:border-gray-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="run-standard-replay-btn"
            >
              <Play className="w-10 h-10" />
              <div className="text-left">
                <div className="text-lg font-semibold">Run Standard Replay</div>
                <div className="text-sm text-gray-400">Single-pass backtest with current fixed weights</div>
              </div>
            </button>

            {/* Button 2: Strategy Optimizer */}
            <button
              onClick={handleRunOptimizer}
              disabled={!status.has_data && !selectedServerFile || status.state === 'playing' || isOptimizing}
              className={`p-6 rounded-xl border transition-all flex items-center gap-4 ${
                activeMode === 'optimizer' 
                  ? 'bg-yellow-600/20 border-yellow-500 text-yellow-400' 
                  : 'bg-[#12121a] border-gray-800 hover:border-gray-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="run-optimizer-btn"
            >
              {isOptimizing ? (
                <Loader2 className="w-10 h-10 animate-spin" />
              ) : (
                <Zap className="w-10 h-10" />
              )}
              <div className="text-left">
                <div className="text-lg font-semibold">Run Strategy Optimizer</div>
                <div className="text-sm text-gray-400">Grid search for optimal weight configuration</div>
              </div>
            </button>

            {/* SESSION 104: Weight History Button */}
            <button
              onClick={() => setActiveTab(activeTab === 'history' ? 'optimizer' : 'history')}
              className={`p-6 rounded-xl border transition-all flex items-center gap-4 ${
                activeTab === 'history' 
                  ? 'bg-violet-600/20 border-violet-500 text-violet-400' 
                  : 'bg-[#12121a] border-gray-800 hover:border-gray-700 text-white'
              }`}
              data-testid="weight-history-btn"
            >
              <BarChart3 className="w-10 h-10" />
              <div className="text-left">
                <div className="text-lg font-semibold">Weight History</div>
                <div className="text-sm text-gray-400">View saved optimizations & pattern analysis</div>
              </div>
            </button>

            {/* SESSION 146t: Hierarchy Backtest tab */}
            <button
              onClick={() => setActiveTab(activeTab === 'hierarchy_backtest' ? 'optimizer' : 'hierarchy_backtest')}
              className={`p-6 rounded-xl border transition-all flex items-center gap-4 ${
                activeTab === 'hierarchy_backtest'
                  ? 'bg-amber-600/20 border-amber-500 text-amber-400'
                  : 'bg-[#12121a] border-gray-800 hover:border-gray-700 text-white'
              }`}
              data-testid="hierarchy-backtest-btn"
            >
              <Target className="w-10 h-10" />
              <div className="text-left">
                <div className="text-lg font-semibold">Hierarchy Backtest</div>
                <div className="text-sm text-gray-400">LQP replay + parameter sweep on persisted footprints</div>
              </div>
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {error}
            </div>
          )}

          {/* Dynamic Results Display */}
          {activeTab === 'hierarchy_backtest' ? (
            <HierarchyBacktestTab />
          ) : activeTab === 'history' ? (
            /* SESSION 104: Weight History Tab */
            <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-violet-400" />
                  Optimization History
                </h2>
                <div className="flex items-center gap-3">
                  {/* Filters */}
                  <select
                    value={historyFilter.symbol}
                    onChange={(e) => setHistoryFilter(prev => ({ ...prev, symbol: e.target.value }))}
                    className="bg-[#0a0a0f] border border-gray-700 rounded px-3 py-1.5 text-sm"
                  >
                    <option value="">All Symbols</option>
                    <option value="NIFTY">NIFTY</option>
                    <option value="BANKNIFTY">BANKNIFTY</option>
                  </select>
                  <select
                    value={historyFilter.days}
                    onChange={(e) => setHistoryFilter(prev => ({ ...prev, days: parseInt(e.target.value) }))}
                    className="bg-[#0a0a0f] border border-gray-700 rounded px-3 py-1.5 text-sm"
                  >
                    <option value={30}>Last 30 Days</option>
                    <option value={60}>Last 60 Days</option>
                    <option value={90}>Last 90 Days</option>
                    <option value={365}>Last Year</option>
                  </select>
                  <button
                    onClick={fetchHistory}
                    disabled={loadingHistory}
                    className="p-2 bg-gray-800 hover:bg-gray-700 rounded"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Pattern Analysis Summary */}
              {historyPatterns && (historyPatterns.NIFTY || historyPatterns.BANKNIFTY) && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {historyPatterns.NIFTY && (
                    <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <h4 className="text-sm font-semibold text-blue-400 mb-2">NIFTY Averages ({historyPatterns.NIFTY.count} records)</h4>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.NIFTY.avg_weights.order_flow}%</div>
                          <div className="text-gray-400">OF</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.NIFTY.avg_weights.profile_context}%</div>
                          <div className="text-gray-400">PC</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.NIFTY.avg_weights.profile_levels}%</div>
                          <div className="text-gray-400">PL</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.NIFTY.avg_weights.gex}%</div>
                          <div className="text-gray-400">GEX</div>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-400">
                        Avg P&L: <span className={historyPatterns.NIFTY.avg_pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {historyPatterns.NIFTY.avg_pnl >= 0 ? '+' : ''}{historyPatterns.NIFTY.avg_pnl} pts
                        </span> | Win Rate: {historyPatterns.NIFTY.avg_win_rate}%
                      </div>
                    </div>
                  )}
                  {historyPatterns.BANKNIFTY && (
                    <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <h4 className="text-sm font-semibold text-orange-400 mb-2">BANKNIFTY Averages ({historyPatterns.BANKNIFTY.count} records)</h4>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.BANKNIFTY.avg_weights.order_flow}%</div>
                          <div className="text-gray-400">OF</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.BANKNIFTY.avg_weights.profile_context}%</div>
                          <div className="text-gray-400">PC</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.BANKNIFTY.avg_weights.profile_levels}%</div>
                          <div className="text-gray-400">PL</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{historyPatterns.BANKNIFTY.avg_weights.gex}%</div>
                          <div className="text-gray-400">GEX</div>
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-400">
                        Avg P&L: <span className={historyPatterns.BANKNIFTY.avg_pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {historyPatterns.BANKNIFTY.avg_pnl >= 0 ? '+' : ''}{historyPatterns.BANKNIFTY.avg_pnl} pts
                        </span> | Win Rate: {historyPatterns.BANKNIFTY.avg_win_rate}%
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* History Table */}
              {loadingHistory ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-400" />
                  <p className="text-gray-400 mt-2">Loading history...</p>
                </div>
              ) : historyRecords.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No optimization history found.</p>
                  <p className="text-sm">Run optimizations and save them to build your history.</p>
                </div>
              ) : (
                <div className="overflow-auto max-h-[500px]">
                  <table className="w-full text-sm">
                    <thead className="text-gray-400 border-b border-gray-800 sticky top-0 bg-[#12121a]">
                      <tr>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-left py-2 px-3">Symbol</th>
                        <th className="text-center py-2 px-3">OF</th>
                        <th className="text-center py-2 px-3">PC</th>
                        <th className="text-center py-2 px-3">PL</th>
                        <th className="text-center py-2 px-3">GEX</th>
                        <th className="text-center py-2 px-3">Trades</th>
                        <th className="text-right py-2 px-3">P&L</th>
                        <th className="text-right py-2 px-3">Win%</th>
                        <th className="text-left py-2 px-3">Regime</th>
                        <th className="text-center py-2 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyRecords.map((record) => {
                        const isExpiry = isExpiryDay(record.date_analyzed, record.symbol);
                        const totalTrades = record.metrics?.total_trades || 0;
                        const winRate = record.metrics?.win_rate || 0;
                        const winners = Math.round(totalTrades * (winRate / 100));
                        const losers = totalTrades - winners;
                        return (
                          <tr 
                            key={record.id} 
                            className={`border-b border-gray-800/50 hover:bg-gray-800/30 ${
                              isExpiry ? 'bg-amber-500/5' : ''
                            }`}
                          >
                            <td className="py-2 px-3 font-mono text-xs">
                              <div className="flex items-center gap-2">
                                <span>{record.date_analyzed}</span>
                                {isExpiry && (
                                  <span 
                                    className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-bold rounded border border-amber-500/30"
                                    title={`${record.symbol} Weekly Expiry`}
                                  >
                                    EXP
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                record.symbol === 'NIFTY' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'
                              }`}>
                                {record.symbol}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-center font-mono">{record.weights?.order_flow}%</td>
                            <td className="py-2 px-3 text-center font-mono">{record.weights?.profile_context}%</td>
                            <td className="py-2 px-3 text-center font-mono">{record.weights?.profile_levels}%</td>
                            <td className="py-2 px-3 text-center font-mono">{record.weights?.gex}%</td>
                            <td className="py-2 px-3 text-center font-mono text-xs">
                              <span className="text-white">{totalTrades}</span>
                              <span className="text-gray-500 mx-1">|</span>
                              <span className="text-green-400">{winners}W</span>
                              <span className="text-gray-500">/</span>
                              <span className="text-red-400">{losers}L</span>
                            </td>
                            <td className={`py-2 px-3 text-right font-mono font-bold ${
                              record.metrics?.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {record.metrics?.total_pnl >= 0 ? '+' : ''}{record.metrics?.total_pnl?.toFixed(1)}
                            </td>
                            <td className="py-2 px-3 text-right font-mono">{record.metrics?.win_rate?.toFixed(0)}%</td>
                            <td className="py-2 px-3 text-xs text-gray-400">{record.regime?.replace(/_/g, ' ')}</td>
                            <td className="py-2 px-3 text-center">
                              <button
                                onClick={() => deleteHistoryRecord(record.id)}
                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded"
                                title="Delete record"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : activeMode === 'optimizer' && optimizerResult ? (
            renderStrategyCard()
          ) : activeMode === 'standard' || status.state !== 'idle' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Panel - Controls */}
              <div className="lg:col-span-1 space-y-6">
                {/* Playback Controls */}
                <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-400" />
                    Playback Controls
                  </h2>
                  
                  {/* Status */}
                  <div className="mb-4 p-3 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className={`font-medium uppercase ${getStateColor(status.state)}`}>
                        {status.state}
                      </span>
                    </div>
                    
                    {status.state === 'playing' && (
                      <div className="mt-2">
                        <div className="flex justify-between text-sm text-gray-400 mb-1">
                          <span>Progress</span>
                          <span>{status.progress_percent || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${status.progress_percent || 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Speed Selection */}
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Playback Speed</label>
                    <div className="flex gap-2">
                      {[1, 5, 10, 25, 50].map(speed => (
                        <button
                          key={speed}
                          onClick={() => handleSpeedChange(speed)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                            ${selectedSpeed === speed 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                          data-testid={`speed-${speed}x-btn`}
                        >
                          {speed}x
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Control Buttons */}
                  <div className="flex gap-3">
                    {status.state === 'playing' ? (
                      <button
                        onClick={handlePause}
                        className="flex-1 py-3 bg-yellow-600 hover:bg-yellow-700 
                                   rounded-lg font-medium flex items-center justify-center gap-2"
                        data-testid="pause-btn"
                      >
                        <Pause className="w-5 h-5" />
                        Pause
                      </button>
                    ) : status.state === 'paused' ? (
                      <button
                        onClick={handleResume}
                        className="flex-1 py-3 bg-green-600 hover:bg-green-700 
                                   rounded-lg font-medium flex items-center justify-center gap-2"
                        data-testid="resume-btn"
                      >
                        <Play className="w-5 h-5" />
                        Resume
                      </button>
                    ) : null}
                    
                    <button
                      onClick={handleStop}
                      disabled={status.state === 'idle'}
                      className="py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 
                                 rounded-lg font-medium flex items-center justify-center gap-2"
                      data-testid="stop-btn"
                    >
                      <Square className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800">
                  <h2 className="text-lg font-semibold mb-4">Session Stats</h2>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Ticks Processed</span>
                      <span className="font-mono">
                        {(status.processed_ticks || 0).toLocaleString()} / {(status.total_ticks || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Signals Generated</span>
                      <span className="font-mono text-blue-400">{status.signals_generated || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Current Speed</span>
                      <span className="font-mono">{status.playback_speed || 1}x</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Signal Log */}
              <div className="lg:col-span-2">
                <div className="bg-[#12121a] rounded-xl p-6 border border-gray-800 h-full">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-400" />
                    Replay Signal Log
                    <span className="text-sm text-gray-400 font-normal">
                      ({signals.length} signals)
                    </span>
                  </h2>
                  
                  {signals.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No signals yet. Start replay to see signals.</p>
                    </div>
                  ) : (
                    <div className="overflow-auto max-h-[600px]">
                      <table className="w-full text-sm">
                        <thead className="text-gray-400 border-b border-gray-800 sticky top-0 bg-[#12121a]">
                          <tr>
                            <th className="text-left py-2 px-3">Time</th>
                            <th className="text-left py-2 px-3">Signal</th>
                            <th className="text-right py-2 px-3">Score</th>
                            <th className="text-right py-2 px-3">EMA</th>
                            <th className="text-right py-2 px-3">Price</th>
                            <th className="text-left py-2 px-3">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {signals.map((signal, idx) => (
                            <tr key={idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                              <td className="py-2 px-3 text-gray-400 font-mono text-xs">
                                {new Date(signal.timestamp).toLocaleTimeString()}
                              </td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium border ${getSignalColor(signal.signal_type)}`}>
                                  {signal.signal_type === 'BUY' && <TrendingUp className="w-3 h-3 inline mr-1" />}
                                  {signal.signal_type === 'SELL' && <TrendingDown className="w-3 h-3 inline mr-1" />}
                                  {signal.signal_type === 'VETO' && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                                  {signal.signal_type}
                                </span>
                              </td>
                              <td className={`py-2 px-3 text-right font-mono ${signal.score > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {signal.score > 0 ? '+' : ''}{signal.score}
                              </td>
                              <td className={`py-2 px-3 text-right font-mono ${signal.ema_score > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {signal.ema_score > 0 ? '+' : ''}{signal.ema_score}
                              </td>
                              <td className="py-2 px-3 text-right font-mono text-gray-300">
                                {signal.entry_price?.toLocaleString()}
                              </td>
                              <td className="py-2 px-3 text-gray-500 text-xs max-w-[200px] truncate">
                                {signal.veto_reason || signal.breakdown?.signal_state || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Initial state - show guidance
            <div className="text-center py-12 text-gray-500">
              <Info className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">Load historical data to begin</p>
              <p className="text-sm">Select a recorded file or upload a CSV, then choose an execution mode.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TimeMachine;
